import { useState } from "react";
import api from "../lib/api";
import { usePipeline } from "../lib/pipeline";
import { useFetch, Card, Badge } from "../components/Shared";
import PageHeader, { PageBody } from "../components/Shared";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Database, ArrowsClockwise } from "@phosphor-icons/react";

const STATUS_TONES = { active: "forest", paused: "neutral", error: "signal" };

export default function LeadSources() {
  const { data, loading, reload } = useFetch(() => api.get("/lead-sources").then((r) => r.data), []);
  const [edit, setEdit] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const items = data || [];

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta fuente de leads?")) return;
    try { await api.delete(`/lead-sources/${id}`); toast.success("Eliminado"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const syncNow = async (id) => {
    try {
      const { data: r } = await api.post(`/lead-sources/${id}/sync-now`);
      toast[r.ok ? "success" : "error"](r.message || r.status);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <>
      <PageHeader title="Fuentes de leads"
        subtitle="Google Sheets · una por pipeline. Polling automático e ingesta unificada.">
        <button onClick={() => setShowCreate(true)} data-testid="add-source-btn"
          className="bg-terracotta text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={14} /> Agregar fuente
        </button>
      </PageHeader>
      <PageBody>
        {loading ? <div className="text-carbon-500">Cargando...</div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((s) => (
              <Card key={s.id} testid={`src-card-${s.id}`} className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-heading text-lg flex items-center gap-2">
                      <Database size={16} className="text-terracotta" />
                      {s.label}
                    </div>
                    <div className="text-[11px] text-carbon-500 mt-1">{s.source_type}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={s.pipeline_slug === "founder" ? "terracotta" : "forest"}>{s.pipeline_slug}</Badge>
                    <Badge tone={STATUS_TONES[s.status] || "neutral"}>{s.status}</Badge>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <Row k="Sheet ID" v={s.sheet_id || "—"} mono />
                  <Row k="Range" v={s.sheet_range || "—"} mono />
                  <Row k="Service Account" v={s.service_account_email || "—"} mono />
                  <Row k="Polling" v={`${s.polling_interval_seconds}s`} />
                  <Row k="Última sync" v={s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : "Nunca"} />
                  <Row k="Leads importados" v={s.total_leads_imported || 0} />
                  {s.last_sync_error && (
                    <div className="bg-signal/10 text-signal rounded p-2 text-[11px] mt-2 leading-relaxed">
                      ⚠ {s.last_sync_error}
                    </div>
                  )}
                  {s.notes && (
                    <div className="bg-page rounded p-2 mt-2 text-[11px] text-carbon-500 leading-relaxed">{s.notes}</div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => syncNow(s.id)} data-testid={`sync-now-${s.id}`}
                    className="text-xs px-2.5 py-1 border border-forest text-forest rounded hover:bg-forest/5 flex items-center gap-1.5">
                    <ArrowsClockwise size={12} /> Sincronizar ahora
                  </button>
                  <button onClick={() => setEdit(s)} data-testid={`edit-source-${s.id}`}
                    className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-page flex items-center gap-1.5">
                    <PencilSimple size={12} /> Editar
                  </button>
                  <button onClick={() => remove(s.id)} className="text-xs px-2.5 py-1 text-signal hover:bg-signal/5 rounded ml-auto">
                    <Trash size={12} />
                  </button>
                </div>
              </Card>
            ))}
            {items.length === 0 && (
              <Card className="p-8 text-center text-carbon-500 md:col-span-2">
                No hay fuentes configuradas todavía.
              </Card>
            )}
          </div>
        )}
      </PageBody>
      {(showCreate || edit) && (
        <SourceForm initial={edit} onClose={() => { setEdit(null); setShowCreate(false); }} onSaved={() => { reload(); setEdit(null); setShowCreate(false); }} />
      )}
    </>
  );
}

const Row = ({ k, v, mono }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-carbon-500 shrink-0">{k}</span>
    <span className={`text-carbon-700 truncate ${mono ? "font-mono text-[11px]" : ""}`} title={String(v)}>{v}</span>
  </div>
);

function SourceForm({ initial, onClose, onSaved }) {
  const { pipelines } = usePipeline();
  const selectablePipelines = (pipelines || []).filter((p) => p.slug !== "all");
  const [form, setForm] = useState({
    label: initial?.label || "",
    pipeline_slug: initial?.pipeline_slug || "founder",
    source_type: initial?.source_type || "google_sheets",
    sheet_id: initial?.sheet_id || "",
    sheet_range: initial?.sheet_range || "Hoja1!A:F",
    service_account_email: initial?.service_account_email || "",
    service_account_json: initial?.service_account_json || "",
    polling_interval_seconds: initial?.polling_interval_seconds || 300,
    status: initial?.status || "paused",
    default_lead_origin: initial?.default_lead_origin || "",
    notes: initial?.notes || "",
    column_mapping: initial?.column_mapping || { Nombre: "name", WhatsApp: "whatsapp" },
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial) await api.patch(`/lead-sources/${initial.id}`, form);
      else await api.post(`/lead-sources`, form);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="source-form">
        <div className="font-heading text-lg mb-4">{initial ? "Editar fuente" : "Agregar fuente de leads"}</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            ["label", "Etiqueta", "Sheet · Founders MX", "col-span-2"],
            ["sheet_id", "Spreadsheet ID", "1AbcDef…XYZ", ""],
            ["sheet_range", "Rango", "Hoja1!A:F", ""],
            ["service_account_email", "Service Account Email", "...@project.iam.gserviceaccount.com", "col-span-2"],
          ].map(([k, lbl, ph, cls]) => (
            <label key={k} className={`block ${cls}`}>
              <span className="text-[11px] uppercase tracking-wide text-carbon-500">{lbl}</span>
              <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={ph} data-testid={`source-${k}-input`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-xs" />
            </label>
          ))}
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Pipeline</span>
            <select value={form.pipeline_slug} onChange={(e) => setForm({ ...form, pipeline_slug: e.target.value })}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              {selectablePipelines.map((p) => (
                <option key={p.slug} value={p.slug}>{p.short || p.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Polling (seg)</span>
            <input type="number" value={form.polling_interval_seconds}
              onChange={(e) => setForm({ ...form, polling_interval_seconds: parseInt(e.target.value) || 300 })}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Service Account JSON (pegá el JSON completo de Google Cloud)</span>
            <textarea value={form.service_account_json || ""} onChange={(e) => setForm({ ...form, service_account_json: e.target.value })}
              placeholder='{"type":"service_account","project_id":"...","private_key":"..."}'
              data-testid="source-sa-json"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-[10px]" rows={4} />
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Column mapping (JSON: header_sheet → user_field)</span>
            <textarea value={JSON.stringify(form.column_mapping, null, 2)}
              onChange={(e) => { try { setForm({ ...form, column_mapping: JSON.parse(e.target.value) }); } catch (_) {} }}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-[10px]" rows={4} />
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Origen por defecto del lead (opcional)</span>
            <input value={form.default_lead_origin || ""} onChange={(e) => setForm({ ...form, default_lead_origin: e.target.value })}
              placeholder="ej: sheet_founders_mx" data-testid="source-default-origin-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Notas</span>
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-xs" rows={2} />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-carbon-500">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="source-save-btn"
            className="text-sm bg-terracotta text-white px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
